import { StockForgeApi } from "../api/client";
import { isProxyUrl, proxyFetch } from "../api/proxyFetch";
import { getApiConfig, loadProxyUrl, saveProxyUrl } from "../config";
import { showToast } from "./toast";

export type ProxyHealthStatus = "ok" | "error" | "unknown" | "checking";

export interface ProxyHealthResult {
  status: ProxyHealthStatus;
  message: string;
}

const MODAL_ID = "sfe-proxy-modal";
const HEALTH_POLL_MS = 10 * 60 * 1000;
const HEALTH_FETCH_MS = 8_000;
const FULL_CHECK_FETCH_MS = 20_000;

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await proxyFetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

function healthError(err: unknown): ProxyHealthResult {
  if (err instanceof Error && err.name === "AbortError") {
    return { status: "error", message: "Proxy timeout — check URL" };
  }
  return {
    status: "error",
    message: err instanceof Error ? err.message : String(err),
  };
}

function normalizeOrigin(url: string): string {
  return url.trim().replace(/\/$/, "");
}

/** Extension on thestockforge.com must set an external proxy; web/mobile on the Linode host use same origin. */
function resolveProxyBaseUrl(baseUrl: string): string | null {
  const normalized = normalizeOrigin(baseUrl);
  if (!normalized) return null;

  const origin = normalizeOrigin(window.location.origin);
  if (normalized !== origin) return normalized;
  return isProxyUrl(origin) ? normalized : null;
}

/** Quick poll uses `/health` only; full test also hits wheel meta upstream. */
export async function checkProxyHealth(
  opts: { full?: boolean } = {},
): Promise<ProxyHealthResult> {
  try {
    const cfg = getApiConfig();
    const proxyBase = resolveProxyBaseUrl(cfg.baseUrl);
    if (!proxyBase) {
      return { status: "error", message: "Proxy URL not set" };
    }

    const health = await fetchWithTimeout(
      `${proxyBase}/health`,
      { cache: "no-store" },
      opts.full ? FULL_CHECK_FETCH_MS : HEALTH_FETCH_MS,
    );
    if (!health.ok) {
      return { status: "error", message: `Health HTTP ${health.status}` };
    }

    if (!opts.full) {
      return { status: "ok", message: "Proxy reachable" };
    }

    const api = new StockForgeApi({
      baseUrl: proxyBase,
    });
    await api.getWheelMeta({ bypassCache: true });
    return { status: "ok", message: "Proxy healthy" };
  } catch (err) {
    return healthError(err);
  }
}

export function openProxySettingsModal(): void {
  let overlay = document.getElementById(MODAL_ID);
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.setAttribute("data-sfe-injected", "true");
    overlay.innerHTML = `
      <div class="sfe-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-proxy-modal-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-proxy-modal-title">API Proxy</h3>
          <button type="button" class="sfe-modal-close" data-action="close" aria-label="Close">×</button>
        </div>
        <label class="sfe-modal-field">
          Proxy URL
          <input type="url" class="sfe-api-key-input sfe-proxy-url" placeholder="https://api.yourdomain.com" />
        </label>
        <p class="sfe-api-hint">
          Sign in via Stock Forge+ Settings. Your Stock Forge API key lives on the server
          (or under your account). The app talks to this proxy, which forwards to Stock Forge.
        </p>
        <div class="sfe-modal-status" data-panel-status>Ready</div>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-btn" data-action="test-api">Test proxy / health</button>
          <button type="button" class="sfe-btn sfe-btn-primary" data-action="save">Save</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) closeProxySettingsModal();
    });

    overlay.querySelector('[data-action="close"]')?.addEventListener("click", () => {
      closeProxySettingsModal();
    });

    const urlInput = overlay.querySelector<HTMLInputElement>(".sfe-proxy-url")!;
    const status = overlay.querySelector<HTMLElement>("[data-panel-status]")!;

    const persist = () => {
      if (urlInput.value.trim()) saveProxyUrl(urlInput.value);
    };

    overlay.querySelector('[data-action="save"]')?.addEventListener("click", () => {
      persist();
      status.textContent = "Saved";
      status.className = "sfe-modal-status sfe-modal-status-ok";
      window.dispatchEvent(new CustomEvent("sfe:proxy-settings-saved"));
      showToast("Proxy settings saved.", "success");
      closeProxySettingsModal();
    });

    overlay.querySelector('[data-action="test-api"]')?.addEventListener("click", async () => {
      persist();
      status.textContent = "Fetching…";
      status.className = "sfe-modal-status";
      const result = await checkProxyHealth({ full: true });
      status.textContent =
        result.status === "ok"
          ? `OK — ${result.message}`
          : `Error: ${result.message}`;
      status.className =
        result.status === "ok"
          ? "sfe-modal-status sfe-modal-status-ok"
          : "sfe-modal-status sfe-modal-status-err";
      showToast(
        result.status === "ok" ? result.message : `Proxy error: ${result.message}`,
        result.status === "ok" ? "success" : "error",
      );
      window.dispatchEvent(
        new CustomEvent("sfe:proxy-health", { detail: result }),
      );
    });
  }

  const urlInput = overlay.querySelector<HTMLInputElement>(".sfe-proxy-url")!;
  const storedUrl = loadProxyUrl();
  if (storedUrl) urlInput.value = storedUrl;

  overlay.classList.add("sfe-modal-open");
  urlInput.focus();
}

export function closeProxySettingsModal(): void {
  document.getElementById(MODAL_ID)?.classList.remove("sfe-modal-open");
}

export function startProxyHealthPolling(
  onUpdate: (result: ProxyHealthResult) => void,
): () => void {
  let cancelled = false;

  const run = async () => {
    if (cancelled) return;
    onUpdate({ status: "checking", message: "Checking…" });
    const result = await checkProxyHealth();
    if (!cancelled) onUpdate(result);
  };

  void run();
  const timer = window.setInterval(() => void run(), HEALTH_POLL_MS);

  const onSaved = () => void run();
  window.addEventListener("sfe:proxy-settings-saved", onSaved);

  return () => {
    cancelled = true;
    window.clearInterval(timer);
    window.removeEventListener("sfe:proxy-settings-saved", onSaved);
  };
}

/** @deprecated side panel removed — kept as alias for callers during migration */
export function createSidePanel(): HTMLElement {
  const stub = document.createElement("div");
  stub.hidden = true;
  return stub;
}
